/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * „DANIE BEZ SPRZEDAŻY" — kiedy ta liczba coś znaczy, a kiedy nie znaczy nic
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * To jest siostra reguły z [pustyZbior.ts], ale dotyczy innego przypadku i dlatego stoi osobno.
 * Tam pusty był ZBIÓR WYNIKÓW. Tutaj zbiór wyników jest pełny — katalog ma 13 dań — a pusty jest
 * WYMIAR, po którym je oceniamy.
 *
 * Ekran katalogu ogłaszał jako liczbę-bohatera zdanie:
 *
 *     „13 z 13 dań w katalogu nie ma ani jednej pozycji sprzedaży"
 *
 * i podświetlał je barwą uwagi. Arytmetycznie bez zarzutu. Tyle że rejestr pozycji sprzedaży był
 * PUSTY, więc to zdanie nie mówiło niczego o daniach — mówiło, że nie mamy danych. Właściciel
 * czytał je jako „cała karta się nie sprzedaje", czyli diagnozę biznesową, której nikt nie postawił.
 *
 *   ŹRÓDŁO PUSTE   — 0 wierszy sprzedaży w ogóle. Liczba „bez sprzedaży" równa się rozmiarowi
 *                    katalogu Z DEFINICJI i nie niesie żadnej informacji o daniach.
 *   ZMIERZONY BRAK — sprzedaż istnieje, ale te konkretne dania w niej nie występują. Dopiero
 *                    to jest sygnał, na który warto patrzeć.
 *
 * Różnica jest niewidoczna w samej liczbie — rozstrzyga ją dopiero liczebność drugiego zbioru.
 */

export type PodstawaBrakuSprzedazy = 'ZRODLO_SPRZEDAZY_PUSTE' | 'ZMIERZONY_BRAK' | 'BRAK_DAN_BEZ_SPRZEDAZY' | 'NIEZNANA';

export interface WejscieBrakuSprzedazy {
  /** Liczba wierszy w CAŁYM rejestrze sprzedaży, nie tylko dla tych dań. null = nie wiadomo. */
  wierszySprzedazyOgolem: number | null | undefined;
  danWKatalogu: number | null | undefined;
  danBezSprzedazy: number | null | undefined;
}

export interface OcenaBrakuSprzedazy {
  podstawa: PodstawaBrakuSprzedazy;
  /** Czy wolno przedstawiać tę liczbę jako sygnał wymagający uwagi. */
  czySygnal: boolean;
  /** Zdanie dla właściciela — mówi, co ta liczba znaczy, a nie tylko ile wynosi. */
  zdanie: string;
}

export function ocenBrakSprzedazy({
  wierszySprzedazyOgolem,
  danWKatalogu,
  danBezSprzedazy,
}: WejscieBrakuSprzedazy): OcenaBrakuSprzedazy {
  if (wierszySprzedazyOgolem === null || wierszySprzedazyOgolem === undefined) {
    return {
      podstawa: 'NIEZNANA',
      czySygnal: false,
      zdanie:
        'Nie wiadomo, ile w ogóle jest zapisanej sprzedaży, więc z liczby dań „bez sprzedaży" nie da się nic wywnioskować.',
    };
  }

  if (wierszySprzedazyOgolem === 0) {
    return {
      podstawa: 'ZRODLO_SPRZEDAZY_PUSTE',
      czySygnal: false,
      zdanie:
        'Rejestr sprzedaży jest pusty — nie ma w nim ani jednej pozycji. Każde danie w katalogu jest więc ' +
        '„bez sprzedaży" z definicji i ta liczba NIE mówi nic o tym, czy dania się sprzedają.',
    };
  }

  if (!danBezSprzedazy) {
    return {
      podstawa: 'BRAK_DAN_BEZ_SPRZEDAZY',
      czySygnal: false,
      zdanie: 'Każde danie w katalogu ma co najmniej jedną pozycję sprzedaży.',
    };
  }

  const ile = danWKatalogu ?? null;
  return {
    podstawa: 'ZMIERZONY_BRAK',
    czySygnal: true,
    zdanie:
      `Sprzedaż jest zapisana (${wierszySprzedazyOgolem} pozycji), a mimo to ${danBezSprzedazy}` +
      `${ile !== null ? ` z ${ile}` : ''} dań nie występuje w niej ani razu. To jest zmierzony brak, nie brak danych.`,
  };
}
